// ABOUTME: React-Query hooks for per-entity configuration import/export against the tuc-configs API.
// ABOUTME: Exposes export (blob download), import-validate (schema/compat check), and import (upsert) flows.
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import fileDownload from 'js-file-download'

import {api} from '../../../../App.store'
import {EntityType, getEntityApiPath} from '../types'

export interface ImportItemResult {
  status: 'Created' | 'Updated' | 'Failed' | string
  scope: string
  reason: string | null
}

export interface ImportReport {
  entityKey: string
  created: number
  updated: number
  failed: number
  results: ImportItemResult[]
}

export interface ImportCompatibilityReport {
  entityKey: string
  compatible: boolean
  bundleSchemaVersion: string
  targetSchemaVersion: string
  sourceFingerprint: string | null
  targetFingerprint: string
  issues: string[]
}

export interface ExportConfigArgs {
  entityType: EntityType
  countryId: string
  businessLine: string
}

/** Downloads all configurations of one entity (for the country/business line) as a JSON bundle file. */
export const useExportConfig = () =>
  useMutation({
    mutationFn: async ({entityType, countryId, businessLine}: ExportConfigArgs) => {
      const path = getEntityApiPath(entityType)
      const res = await api.get(`/tuc-configs/${path}/export`, {
        params: {countryId, businessLine: businessLine.toUpperCase()},
        responseType: 'blob'
      })
      const stamp = new Date().toISOString().slice(0, 10)
      fileDownload(res.data as Blob, `${path}-${countryId}-${businessLine}-${stamp}.json`)
    }
  })

/** Checks a bundle against this environment (schema version + data-shape fingerprint) without writing. */
export const useValidateImport = () =>
  useMutation({
    mutationFn: async ({entityType, bundle}: {entityType: EntityType; bundle: unknown}) => {
      const path = getEntityApiPath(entityType)
      const res = await api.post<ImportCompatibilityReport>(`/tuc-configs/${path}/import/validate`, bundle)
      return res.data
    }
  })

/** Imports a bundle (create-or-update per scope). Pass force to override a schema-drift rejection. */
export const useImportConfig = (countryId: string, businessLine: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      entityType,
      bundle,
      force
    }: {
      entityType: EntityType
      bundle: unknown
      force?: boolean
    }) => {
      const path = getEntityApiPath(entityType)
      const res = await api.post<ImportReport>(`/tuc-configs/${path}/import`, bundle, {
        params: force ? {force: true} : undefined
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.includes(countryId) &&
          query.queryKey.includes(businessLine)
      })
    }
  })
}

// ── Bulk (multi-entity) import/export ──────────────────────────────────────

export interface BulkEntityImportResult {
  entityKey: string
  accepted: boolean
  compatibility: ImportCompatibilityReport | null
  report: ImportReport | null
  error: string | null
}

export interface BulkImportReport {
  entities: BulkEntityImportResult[]
}

export interface BulkCompatibilityReport {
  entities: ImportCompatibilityReport[]
}

/** Lists the entity keys that support bulk import/export in this environment. */
export const useBulkEntities = () =>
  useQuery<string[]>({
    queryKey: ['bulkEntities'],
    queryFn: async () => {
      const res = await api.get<string[]>('/tuc-configs/bulk/entities')
      return res.data
    },
    staleTime: 30 * 60 * 1000
  })

/** Downloads a single multi-entity bundle for the selected entity keys (all when the list is empty). */
export const useBulkExport = () =>
  useMutation({
    mutationFn: async ({
      entityKeys,
      countryId,
      businessLine
    }: {
      entityKeys: string[]
      countryId: string
      businessLine: string
    }) => {
      const params: Record<string, string> = {countryId, businessLine: businessLine.toUpperCase()}
      if (entityKeys.length > 0) params.entities = entityKeys.join(',')

      const res = await api.get('/tuc-configs/bulk/export', {params, responseType: 'blob'})
      const stamp = new Date().toISOString().slice(0, 10)
      fileDownload(res.data as Blob, `tuc-configs-${countryId}-${businessLine}-${stamp}.json`)
    }
  })

/** Checks each section of a multi-entity bundle against this environment without writing. */
export const useBulkValidateImport = () =>
  useMutation({
    mutationFn: async (bundle: unknown) => {
      const res = await api.post<BulkCompatibilityReport>('/tuc-configs/bulk/import/validate', bundle)
      return res.data
    }
  })

/** Imports a multi-entity bundle. Incompatible sections are skipped unless force is set. */
export const useBulkImport = (countryId: string, businessLine: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({bundle, force}: {bundle: unknown; force?: boolean}) => {
      const res = await api.post<BulkImportReport>('/tuc-configs/bulk/import', bundle, {
        params: force ? {force: true} : undefined
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.includes(countryId) &&
          query.queryKey.includes(businessLine)
      })
    }
  })
}

